import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { TableRowHeight } from '@revolut-internal/ui-kit-table'

import { StorageKey } from 'modules/storage/const'
import { ClientStorage } from 'modules/storage/storage'

const activeTableRowHeightStorage = new ClientStorage<TableRowHeight>(
  StorageKey.QueueHealthTableRowHeight,
)

type TableRowHeightContextValue = {
  activeTableRowHeight: TableRowHeight
  setActiveTableRowHeight: (value: TableRowHeight) => void
}

const TableRowHeightContext = createContext<TableRowHeightContextValue | null>(null)

export const useTableRowHeight = () => {
  const context = useContext(TableRowHeightContext)

  if (!context) {
    throw new Error('`useTableRowHeight` must be used within a `TableRowHeightProvider`')
  }

  return context
}
export const TableRowHeightProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTableRowHeight, setActiveTableRowHeight] = useState(
    activeTableRowHeightStorage.getValue(TableRowHeight.SMALL),
  )

  // TODO: [remove in any next PR] is needed to update agent's local-storage states
  useEffect(() => {
    if (!Object.values(TableRowHeight).includes(activeTableRowHeight)) {
      setActiveTableRowHeight(TableRowHeight.SMALL)
    }
  }, [activeTableRowHeight])

  const handleActiveTableRowHeightChanged = useCallback((value: TableRowHeight) => {
    setActiveTableRowHeight(value)
    activeTableRowHeightStorage.setValue(value)
  }, [])

  const value = useMemo(
    () => ({
      activeTableRowHeight,
      setActiveTableRowHeight: handleActiveTableRowHeightChanged,
    }),
    [activeTableRowHeight, handleActiveTableRowHeightChanged],
  )

  return (
    <TableRowHeightContext.Provider value={value}>
      {children}
    </TableRowHeightContext.Provider>
  )
}
